import type { SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { Actions } from '../../components/product-table/columns/actions-column';
import { CaseQty } from '../../components/product-table/columns/case-qty-column';
import { DOH } from '../../components/product-table/columns/doh-column';
import { ItemCode } from '../../components/product-table/columns/item-code-column';
import { LastReceived } from '../../components/product-table/columns/last-received-column';
import { LastSold } from '../../components/product-table/columns/last-sold-column';
import { MarginPercentage } from '../../components/product-table/columns/margin-percentage-column';
import { OrderLot } from '../../components/product-table/columns/order-lot-column';
import { ProductNameWithLabels } from '../../components/product-table/columns/product-name-with-labels-column';
import { QOHCases } from '../../components/product-table/columns/qoh-cases-column';
import { Reorder } from '../../components/product-table/columns/reorder-column';
import { getCasesSoldColumn } from '../../components/product-table/columns/sales-columns';
import { Size } from '../../components/product-table/columns/size-column';
import { Type } from '../../components/product-table/columns/type-column';
import { VendorName } from '../../components/product-table/columns/vendor-name-column';
import { DepartmentFilter } from '../../components/product-table/filters/department-filter';
import { FiltersPopover } from '../../components/product-table/filters/filters-popover';
import { HideDisabledFilter } from '../../components/product-table/filters/hide-disabled-filter';
import { SizeFilter } from '../../components/product-table/filters/size-filter';
import { SoldInFilter } from '../../components/product-table/filters/sold-in-filter';
import { TypeFilter } from '../../components/product-table/filters/type-filter';
import { VendorNameFilter } from '../../components/product-table/filters/vendor-name-filter';
import { type SortColumnOption, SortPopover } from '../../components/sort-popover';
import type { StrictColumnDef } from '../../components/tanstack-table/column';
import { ReportActions } from '../../components/tanstack-table/report-actions';
import { TanStackTable } from '../../components/tanstack-table/table';
import { useRouteRowSelection } from '../../components/tanstack-table/use-route-row-selection';
import { useTableRef } from '../../components/tanstack-table/use-table-ref';
import { PageToolbar } from '../../components/toolbar/page-toolbar';
import { SearchWithCount } from '../../components/toolbar/search-with-count';
import { ProductTableContext } from '../../contexts/product-table-context';
import type { Product, SaleStatDays } from '../../data/product';
import { type Filters, useFilteredProducts } from '../../hooks/products';
import { productReportsLayout } from '../../router';

const defaultFilters: Filters = {
  soldIn: 365,
  minimumInventory: 0,
};

export function PopularityReport() {
  const products = productReportsLayout.useLoaderData();
  const soldInDays: SaleStatDays[] = [30, 60, 90, 180, 365];

  const { filters, setFilters, filteredProducts, baselineCount } = useFilteredProducts(
    products,
    defaultFilters
  );
  const { rowSelection, onRowSelectionChange, onRowClick } = useRouteRowSelection();
  const [table, onTableReady] = useTableRef<Product>();

  // The cases sold column changes based on the soldIn filter
  const casesSoldColumn = useMemo(
    () => getCasesSoldColumn(filters.soldIn || 365),
    [filters.soldIn]
  );

  // Column definitions
  const columns = useMemo<StrictColumnDef<Product>[]>(() => {
    return [
      VendorName,
      ItemCode,
      ProductNameWithLabels,
      Type,
      Size,
      CaseQty,
      OrderLot,
      LastSold,
      LastReceived,
      QOHCases,
      DOH,
      casesSoldColumn,
      MarginPercentage,
      Reorder,
      Actions,
    ];
  }, [casesSoldColumn]);

  // Sort options for the dropdown (using column ids)
  const sortColumnOptions = useMemo<SortColumnOption[]>(
    () => [
      { id: LastSold.id, label: LastSold.id },
      { id: LastReceived.id, label: LastReceived.id },
      { id: casesSoldColumn.id, label: casesSoldColumn.id },
    ],
    [casesSoldColumn]
  );

  // TanStack sorting state - default to cases sold descending
  const [sorting, setSorting] = useState<SortingState>([{ id: casesSoldColumn.id, desc: true }]);

  // When cases sold column changes (due to soldIn filter), update sorting if it was the active sort
  const handleSortingChange = useMemo(() => {
    return (updaterOrValue: SortingState | ((prev: SortingState) => SortingState)) => {
      setSorting((prev) => {
        const newSorting =
          typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;
        // If the current sort is on a "cases sold" column and the id changed, update it
        if (newSorting.length > 0 && newSorting[0].id.toLowerCase().endsWith('cases sold')) {
          return [{ id: casesSoldColumn.id, desc: newSorting[0].desc }];
        }

        return newSorting;
      });
    };
  }, [casesSoldColumn.id]);

  const contextValue = useMemo(() => {
    return { filters, setFilters, filteredProducts };
  }, [filters, setFilters, filteredProducts]);

  return (
    <>
      <ProductTableContext.Provider value={contextValue}>
        <PageToolbar
          left={
            <>
              <FiltersPopover defaultFilters={defaultFilters}>
                <HideDisabledFilter />
                <SoldInFilter overrideSoldInDays={soldInDays} />
                <VendorNameFilter products={products} />
                <DepartmentFilter products={products} />
                <TypeFilter products={products} />
                <SizeFilter products={products} />
              </FiltersPopover>
              <SortPopover
                sorting={sorting}
                onSortingChange={setSorting}
                sortColumnOptions={sortColumnOptions}
              />
            </>
          }
          right={
            <>
              <SearchWithCount
                quantity={filteredProducts.length}
                total={baselineCount}
                noun="products"
              />
              <ReportActions table={table} title="Popularity" />
            </>
          }
        />
      </ProductTableContext.Provider>
      <TanStackTable
        data={filteredProducts}
        columns={columns}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        onRowClick={onRowClick}
        getRowId={(product) => product.id}
        onTableReady={onTableReady}
      />
    </>
  );
}
